import axios, { isAxiosError } from 'axios';
import { emitQuotaExceeded, isQuotaExceededBody } from './quotaEvents';
import type {
  ApiItemResponse,
  Inspection,
  NotificationPreferences,
  Plan,
  ReportPreset,
  ReportSchema,
  Subscription,
  SubscriptionPlan,
  User,
} from '../types';
import { EMPTY_BRANDING_OVERLAY, type BrandingOverlay } from './branding';

const baseURL = import.meta.env.VITE_API_URL ?? '/api';

type AuthResponse = {
  token: string;
  refreshToken?: string;
  user: User;
  // Set only when signup chose Organization + Pro (story 2-13) — the app
  // redirects here to complete Stripe checkout instead of routing to /app.
  checkoutUrl?: string | null;
};

// Signup fork (story 2-13). accountType defaults to 'personal' (a solo free
// workspace); 'organization' requires orgName and may pick a plan.
export type SignupPayload = {
  name: string;
  email: string;
  password: string;
  accountType?: 'personal' | 'organization';
  orgName?: string;
  planSlug?: 'free' | 'pro';
};

export const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Quota-exceeded interceptor (story 2-16). Every quota-capped endpoint the UI
// calls today (report create, PDF regenerate, the photo pipeline) goes through
// this legacy instance — and the generated contract clients reuse it too — so
// ONE response interceptor routes every quota 402 to the DiscoveryProvider's
// quota teaser. Chosen over per-call handling as the least invasive shared
// hook: future capped endpoints are covered for free. The error still rejects,
// so callers keep their loading/error handling; they only skip their own
// paywall fallback for quota-shaped 402s (see quotaEvents.isQuotaError).
api.interceptors.response.use(undefined, (error: unknown) => {
  if (isAxiosError(error) && error.response?.status === 402 && isQuotaExceededBody(error.response.data)) {
    const { metric, used, limit, period, upgradeUrl } = error.response.data;
    emitQuotaExceeded({ metric, used, limit, period: period ?? null, ...(upgradeUrl ? { upgradeUrl } : {}) });
  }
  return Promise.reject(error);
});

let currentToken: string | null = null;

export function setAuthToken(token: string): void {
  currentToken = token;
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
}

export function clearAuthToken(): void {
  currentToken = null;
  delete api.defaults.headers.common.Authorization;
}

export function getAuthToken(): string | null {
  return currentToken;
}

// -------- Auth --------
export async function signupUser(payload: SignupPayload): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/signup', payload);
  return response.data;
}

export async function loginUser(payload: { email: string; password: string }): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/login', payload);
  return response.data;
}

export async function fetchCurrentUser(): Promise<User> {
  const response = await api.get<{ user: User }>('/auth/me');
  return response.data.user;
}

export type AuthProviderInfo = {
  key: string;
  label: string;
  kind: 'local' | 'oauth';
  featureKey: string;
};

// Story 6-9 scope item 3 (pre-auth branding): `GET /auth/providers` now
// carries `branding` alongside `providers` — both derived from the SAME
// unauthenticated email→user→org resolution (`auth.service#resolveAuthProviders`/
// `#resolveAuthBranding`, see the latter's own header for the full
// investigation of why this reuses that hook rather than inventing
// slug/subdomain routing, and the enumeration trade-off it accepts).
export type AuthProvidersResolution = {
  providers: AuthProviderInfo[];
  branding: BrandingOverlay;
};

// Returns the FULL resolution ({providers, branding}), not just the array —
// `LoginPage.tsx` and `OAuthProviderButtons.tsx` deliberately share ONE
// react-query cache entry for this call (identical `['auth','providers',email]`
// key, LoginPage's own comment explains why), so both callers must read the
// SAME response shape or whichever component's query happens to populate the
// cache first silently determines what the other one sees.
export async function fetchAuthProviders(email?: string): Promise<AuthProvidersResolution> {
  // With an email the backend resolves the org's flags and hides any sign-in
  // method disabled for that org (story 1F-2); without it, the defaults.
  const params = email ? { params: { email } } : undefined;
  const response = await api.get<AuthProvidersResolution>('/auth/providers', params);
  return { providers: response.data.providers, branding: response.data.branding ?? EMPTY_BRANDING_OVERLAY };
}

// OAuth start routes are full-page redirects (GET /auth/:provider), not XHR calls
export function getOAuthStartUrl(providerKey: string): string {
  return new URL(`${baseURL}/auth/${providerKey}`, window.location.origin).toString();
}

export async function forgotPassword(email: string): Promise<void> {
  await api.post('/auth/forgot-password', { email });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await api.post(`/auth/reset-password?token=${encodeURIComponent(token)}`, { password });
}

// `updateOnboarding` (PATCH /account/onboarding, the retired `OnboardingTour`'s
// write path) is gone as of story 2-106 — see `lib/api/activationChecklist.
// gen.ts` (`dismissActivationChecklist`) for its contract-first replacement.

// -------- Properties & Inspections --------
// Properties client is generated from the contract (contracts/properties.js) — import
// listProperties/createProperty/... and propertiesKeys from './api/properties.gen'.

// `fetchInspections` (a hand-rolled GET /inspections) was DELETED by story
// 2-72c, not left orphaned: that route is contract-first now
// (`contracts/inspections.js → listInspections`, `lib/api/inspections.gen.ts`),
// and a second, unreachable description of the same request is exactly the
// drift contract-first exists to stop (DOD §4 "never hand-rolled fetch on the
// frontend"). Its two callers (`ItemsListPage.tsx`, `ActivityPage.tsx`) both
// moved onto the generated `listInspections()` in the same story.

export async function createInspection(payload: {
  propertyId: string;
  scheduledFor?: string;
  rooms?: Array<{ name: string; displayOrder?: number }>;
  reportPresetId?: string;
  // The procedure this inspection follows (P03 §3.2; accepted by the endpoint
  // since story 3-3a, sent by the TypePicker since 3-3b). OPTIONAL: inspections
  // predate types, so an untyped inspection stays a valid record. When present,
  // the server enforces `inspections:create` ∩ `allowedRoleIds.perform` and
  // freezes the type's SLA/severity onto the new inspection.
  inspectionTypeId?: string;
}): Promise<Inspection> {
  const response = await api.post<ApiItemResponse<Inspection>>('/inspections', payload);
  return response.data.data;
}

// getPhotoUploadUrls / registerUploadedPhotos (the presigned upload flow's
// client side) and fetchInspection / PhotoUrl / fetchPhotoUrls /
// completeInspection / fetchReport / generateReport / downloadReport (this
// apiClient copy — NOT the contract-first `downloadReport` in
// `lib/api/reports.gen.ts`, which is untouched) / generateReportPDF were
// retired in story 5-12: every one of them existed only for the legacy
// `ItemDetailPage` and its page-local children (`BulkPhotoUploader`,
// `ReportSummary`), which are retired in the same story. Their replacements —
// capture's own evidence upload, the contract-first `reports.gen.ts` readers —
// are not hand-rolled `apiClient` functions (P18 §4). The backend routes
// behind `registerUploadedPhotos`/`fetchInspection`/`generateReport`/
// `downloadReport`/`generateReportPDF`/`completeInspection` stay mounted —
// each has its own direct backend-test or agent-gateway caller independent of
// this file (`inspectionLifecycle.api.test.js`, `reportQuota.test.js`,
// `reportDetail.authz.test.js`, `agents/src/capabilities/reports.js`) — only
// this now-uncalled client-side copy is gone. `getPhotoUploadUrls`'s and
// `fetchReport`'s and `fetchPhotoUrls`'s backend routes had no other caller
// either and were retired alongside them (see report.route.js /
// inspection.route.js).

// -------- Subscription --------
export async function fetchSubscription(organizationId: string): Promise<Subscription> {
  const response = await api.get<ApiItemResponse<Subscription>>(`/subscriptions/${organizationId}`);
  return response.data.data;
}

export async function upgradeSubscription(organizationId: string): Promise<Subscription> {
  const response = await api.post<ApiItemResponse<Subscription>>(`/subscriptions/${organizationId}/upgrade`);
  return response.data.data;
}

export async function fetchSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  const response = await api.get<{ data: SubscriptionPlan[] }>('/subscriptions/plans');
  return response.data.data;
}

export async function fetchPublicPlans(): Promise<Plan[]> {
  const response = await api.get<{ data: Plan[] }>('/plans/public');
  return response.data.data;
}

export async function createCheckoutSession(
  organizationId: string,
  payload: { priceId: string; cancelUrl?: string }
): Promise<string> {
  const response = await api.post<{ url: string }>(`/subscriptions/${organizationId}/checkout`, payload);
  return response.data.url;
}

// Activates the plan straight from the success-redirect (?session_id=…) —
// idempotent with the webhook, so whichever lands first wins.
export async function confirmCheckoutSession(organizationId: string, sessionId: string): Promise<Subscription> {
  const response = await api.post<ApiItemResponse<Subscription>>(
    `/subscriptions/${organizationId}/checkout/confirm`,
    { sessionId }
  );
  return response.data.data;
}

export async function openBillingPortal(organizationId: string, payload?: { returnUrl?: string }): Promise<string> {
  const response = await api.post<{ url: string }>(`/subscriptions/${organizationId}/portal`, payload ?? {});
  return response.data.url;
}

export async function cancelSubscription(organizationId: string): Promise<Subscription> {
  const response = await api.post<ApiItemResponse<Subscription>>(`/subscriptions/${organizationId}/cancel`);
  return response.data.data;
}

export async function resumeSubscription(organizationId: string): Promise<Subscription> {
  const response = await api.post<ApiItemResponse<Subscription>>(`/subscriptions/${organizationId}/resume`);
  return response.data.data;
}

// -------- Admin --------
// The legacy superadmin dashboard/users/settings helpers were retired with
// their pages in story 2-8 (superseded by the Org Access + Platform consoles).
// assignPlanToOrganization was removed with story 2-22: its backend endpoint
// (POST /admin/plans/:planId/assign) is retired — plan assignment is owner-plane
// now (assignOrgPlan in lib/api/platform.gen.ts). fetchAdminPlans/createAdminPlan
// (GET/POST /admin/plans, zero call sites) were retired alongside it in 2-23.

// -------- Account / Notifications --------
export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  const response = await api.get<{ data: NotificationPreferences }>('/account/preferences');
  return response.data.data;
}

export async function updateNotificationPreferences(
  payload: Partial<NotificationPreferences>
): Promise<NotificationPreferences> {
  const response = await api.put<{ data: NotificationPreferences }>('/account/preferences', payload);
  return response.data.data;
}

// -------- Report Presets --------
type CreateReportPresetPayload = {
  name: string;
  description?: string;
  schema?: ReportSchema;
  tags?: string[];
  sampleReport?: File | null;
  isDefault?: boolean;
};

type UpdateReportPresetPayload = {
  name?: string;
  description?: string;
  schema?: ReportSchema;
  tags?: string[];
  isDefault?: boolean;
};

// The library LIST is now `listTemplates()` (lib/api/templates.gen.ts,
// contract-first, story 4-2a) — its item deliberately omits `schema`/
// `versions[]` (P04 §4: the list is for browsing, not editing). This single-row
// GET is what survives here: the one read that still carries the full
// `ReportSchema` a caller needs to hydrate the editor canvas or diff a linked
// template's declared fields (`GET /report-presets/:presetId`, still Joi-backed
// — see the route file's comment on why the legacy CRUD half isn't
// contract-first yet).
export async function fetchReportPreset(presetId: string): Promise<ReportPreset> {
  const response = await api.get<ApiItemResponse<ReportPreset>>(`/report-presets/${presetId}`);
  return response.data.data;
}

export async function createReportPreset(payload: CreateReportPresetPayload): Promise<ReportPreset> {
  const formData = new FormData();
  formData.append('name', payload.name);
  if (payload.description) {
    formData.append('description', payload.description);
  }
  if (payload.schema) {
    formData.append('schema', JSON.stringify(payload.schema));
  }
  if (payload.tags && payload.tags.length > 0) {
    formData.append('tags', JSON.stringify(payload.tags));
  }
  if (payload.sampleReport) {
    formData.append('sampleReport', payload.sampleReport);
  }
  if (typeof payload.isDefault === 'boolean') {
    formData.append('isDefault', String(payload.isDefault));
  }

  const response = await api.post<ApiItemResponse<ReportPreset>>('/report-presets', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data.data;
}

export async function updateReportPreset(presetId: string, payload: UpdateReportPresetPayload): Promise<ReportPreset> {
  const response = await api.patch<ApiItemResponse<ReportPreset>>(`/report-presets/${presetId}`, payload);
  return response.data.data;
}

export async function deleteReportPreset(presetId: string): Promise<void> {
  await api.delete(`/report-presets/${presetId}`);
}

// fetchInspectionJobs / fetchJob (the `/jobs/*` readers, `useInspectionJobs`'s
// only caller) were retired in story 5-12 alongside that hook — job/socket
// progress was page-local to the legacy `ItemDetailPage`. The backend
// `/jobs/*` routes themselves are untouched: they carry POST/PATCH verbs this
// story never called and their own retirement is a separate concern (flagged
// as a finding, not fixed here — see 5-12's done-note in 17-story-map.md).
